export const COMPANY_NAME = 'TransEleven Claims Managers';

export function resolveSignerLine(body: string, signerName: string): string {
  const nameIsCompany = signerName.trim().toLowerCase() === COMPANY_NAME.toLowerCase();
  if (!signerName || nameIsCompany) {
    return body.replace('{signer_name}\n', '');
  }
  return body.replace('{signer_name}', signerName);
}

export function toSetNorm(recipients: { email: string }[] | undefined): Set<string> {
  const s = new Set<string>();
  (recipients || []).forEach(r => {
    if (r.email) s.add(r.email.trim().toLowerCase());
  });
  return s;
}

export function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  let match = true;
  a.forEach(v => { if (!b.has(v)) match = false; });
  return match;
}

export function toSetKey(recipients: { email: string }[] | undefined): string {
  return Array.from(toSetNorm(recipients)).sort().join(',');
}

// Queue <-> detail navigation (amos67). 'new' is the queue's default review
// filter, so only 'all' is encoded — default-state URLs stay identical to the
// pre-amos67 shape.
export type ReviewFilter = 'new' | 'all';

export function parseReviewFilter(param: string | null): ReviewFilter {
  return param === 'all' ? 'all' : 'new';
}

export function buildQueueUrl(tab: string | null | undefined, filter?: string | null): string {
  const params = new URLSearchParams();
  if (tab && tab !== 'all') params.set('tab', tab);
  if (filter === 'all') params.set('filter', 'all');
  const qs = params.toString();
  return qs ? `/convex?${qs}` : '/convex';
}

export function buildRequestUrl(requestId: string, tab: string, filter?: string | null): string {
  const params = new URLSearchParams();
  params.set('tab', tab);
  if (filter === 'all') params.set('filter', 'all');
  return `/convex/request/${requestId}?${params.toString()}`;
}

// ── Gate eligibility (relocated from ConvexLossRun.tsx for testability) ─────
// Structural subset of ConvexRequest — only the fields the predicates read.
export interface GateEligibilityFields {
  triage_class?: string;
  auto_verified?: string;
  skippy_verified?: boolean | string;
  recipient_verified?: boolean | string;
  archived?: boolean;
  archived_at?: string;
  reviewer_notes?: string;
  duplicate_hold?: boolean;
  prior_send_badge?: PriorSendBadge | null;
  client_mismatch_badge?: ClientMismatchBadge | null;
  policy_data?: any;
}

// Amos-103 item 2 (V8 in the Lambda): a FileHandler claim on the requested terms whose client is
// not the policy holder. Blocks every automatic path exactly like a blocking sent-before badge;
// only a per-card Approve (marker carries the fingerprint) sends it. Ruled pairs get no badge.
// `text` is the server's card line, shown verbatim. A badge without the flag blocks (fail closed).
export interface ClientMismatchBadge {
  version?: string;
  policy_ref?: string;
  policy_insured?: string;
  claims?: { claim_number?: string; term?: string; fh_client?: string }[];
  blocks_auto_send?: boolean;
  fingerprint?: string;
  computed_at?: string;
  text?: string;
}

export function clientMismatchBlocksAuto(req: { client_mismatch_badge?: ClientMismatchBadge | null }): boolean {
  return !!req.client_mismatch_badge && req.client_mismatch_badge.blocks_auto_send !== false;
}

export function clientMismatchText(req: { client_mismatch_badge?: ClientMismatchBadge | null }): string {
  const b = req.client_mismatch_badge;
  if (!b) return '';
  if (b.text) return b.text;
  const clients = Array.from(new Set((b.claims || []).map(c => c.fh_client).filter(Boolean))).join(', ');
  return `Client mismatch: FileHandler claims on ${b.policy_ref || 'this policy'} belong to ${clients || 'another client'}, not ${b.policy_insured || 'the policy holder'}.`;
}

// R2v4 (Richard ruling 5, 2026-10-08; option (ii), N = 10): a prior send of the same
// insured+period+term is a badge, not a hold. The backend's single predicate
// (promote_predicate.prior_send_blocks_auto) stamps blocks_auto_send: true for the same firm
// within window_bd business days or a different firm (any age) -> bulk excludes the card and
// only a human per-card approve sends it. false = an older same-firm send: info note only,
// the card stays auto-eligible. A badge without the flag is treated as blocking (fail closed).
export interface PriorSendBadge {
  last_sent_at?: string;
  recipients?: string[];
  recipient_match?: string;
  business_days_ago?: number | null;
  prior_request_id?: string;
  prior_imid?: string;
  relation?: string; // 'same recipients' | 'same firm' | 'different firm'
  send_count?: number;
  blocks_auto_send?: boolean;
  window_bd?: number;
}

export function hasPriorSendBadge(req: { prior_send_badge?: PriorSendBadge | null }): boolean {
  return !!req.prior_send_badge;
}

export function priorSendBlocksAuto(req: { prior_send_badge?: PriorSendBadge | null }): boolean {
  return !!req.prior_send_badge && req.prior_send_badge.blocks_auto_send !== false;
}

// Richard ruling 3 (2026-10-09): the single-card reviewed-approve marker. The Lambda refuses a
// blocking sent-before card unless the approve carries this marker, bound to the card and to the
// badge the user was shown. Only the per-card Approve (after the badge is on screen) sends it; the
// bulk loop never does. Null when the card has no blocking badge (no marker needed).
// Amos-103: a blocking client-mismatch (V8) badge also needs the marker; it then carries the
// badge fingerprint, so a badge that changed since the screen loaded is refused (stale).
export interface ReviewedApproveMarker {
  version: 'r2v4';
  request_id: string;
  badge_last_sent_at: string;
  badge_prior_imid: string;
  client_mismatch_fingerprint?: string;
}

export function reviewedApproveMarker(req: {
  request_id?: string; prior_send_badge?: PriorSendBadge | null; client_mismatch_badge?: ClientMismatchBadge | null;
}): ReviewedApproveMarker | null {
  const ps = priorSendBlocksAuto(req);
  const v8 = clientMismatchBlocksAuto(req);
  if (!req.request_id || (!ps && !v8)) return null;
  const b = (req.prior_send_badge || {}) as PriorSendBadge;
  return {
    version: 'r2v4',
    request_id: req.request_id,
    badge_last_sent_at: ps ? b.last_sent_at || '' : '',
    badge_prior_imid: ps ? b.prior_imid || '' : '',
    ...(v8 ? { client_mismatch_fingerprint: (req.client_mismatch_badge as ClientMismatchBadge).fingerprint || '' } : {}),
  };
}

export function priorSendBadgeText(badge?: PriorSendBadge | null): string {
  if (!badge) return '';
  const date = (badge.last_sent_at || '').slice(0, 10);
  const to = (badge.recipients || []).join(', ') || 'unknown';
  const n = badge.business_days_ago ?? '?';
  const head = `Sent before: ${date} to ${to} (${n} business days ago, ${badge.relation}).`;
  if (badge.blocks_auto_send === false) {
    return `${head} For information: older than ${badge.window_bd ?? 10} business days; automatic send allowed.`;
  }
  return `${head} Approve to send again, or Archive as duplicate.`;
}

export type PriorSendDisplay = { kind: 'block' | 'info'; text: string; className: string; title: string } | null;

// Card chip / detail panel: blocking = amber hold chip; info-only = plain grey note.
// Amos-103 rev 2 ruling 5: replies closed at intake (R7 operator "just sent", R8 bare acknowledgment) are
// closed cards in Completed that cite the Sidecar send they answer. Label them instead of "Manually archived".
export interface AnsweredBy {
  request_id?: string;
  imid?: string;
  sent_at?: string;
  ref?: string;
}

export function archivedReasonLabel(reason?: string, answeredBy?: AnsweredBy | null): string {
  if (!reason) return '';
  if (reason === 'auto_sent') return 'Automatically archived (sent)';
  if (reason === 'answered_reply_operator_confirmation' || reason === 'answered_reply_recipient_ack') {
    const who = reason === 'answered_reply_operator_confirmation'
      ? "Closed at intake: operator's own reply confirming a send"
      : "Closed at intake: recipient's acknowledgment of a send";
    const a = answeredBy || {};
    const ref = a.ref ? ` of ${a.ref}` : '';
    const at = a.sent_at ? ` on ${a.sent_at.slice(0, 16).replace('T', ' ')}Z` : '';
    const rid = a.request_id ? ` (card ${a.request_id.slice(0, 8)})` : '';
    return `${who}${ref}${at}${rid}. Nothing to send.`;
  }
  return 'Manually archived';
}

// Amos-103 promote-fix (Richard 2026-10-12).
// (b) The edit panel sends only the fields the operator actually changed. reviewer_notes is sent only when she
//     edited it, so an empty or unchanged save never stamps "Note added" and the card stays Reviewed.
export interface EditPanelValues {
  ref: string;
  insured: string;
  notes: string;
}

export function editPanelPatch(
  req: { extracted_reference_number?: string | null; extracted_insured_name?: string | null; reviewer_notes?: string | null },
  edits: EditPanelValues,
): Record<string, string> | null {
  const body: Record<string, string> = {};
  if (edits.ref !== (req.extracted_reference_number || '')) body.extracted_reference_number = edits.ref;
  if (edits.insured !== (req.extracted_insured_name || '')) body.extracted_insured_name = edits.insured;
  if (edits.notes !== (req.reviewer_notes || '')) body.reviewer_notes = edits.notes;
  return Object.keys(body).length ? body : null;
}

// (a) "Promote to Ready" and "Verify and promote" make the same correct_recipients call (verify_only). When the
//     server's guards keep the card in Review, it says why; the screen shows that instead of silently doing nothing.
export interface VerifyPromoteResult {
  promoted?: boolean;
  promotion_blocked?: { code?: string; message?: string } | null;
}

export function promotionBlockedMessage(res: VerifyPromoteResult | null | undefined, triageClass?: string): string | null {
  if (triageClass !== 'B' && triageClass !== 'A') return null; // a Ready card is only re-confirmed, never "promoted"
  if (res?.promoted) return null;
  return res?.promotion_blocked?.message
    || 'Not moved to Ready: the server did not promote this card. It stays in Review. Refresh and check the card.';
}

export function currentRecipients(req: { response_recipients?: { to?: { email?: string }[]; cc?: { email?: string }[] } | null }) {
  const pick = (xs?: { email?: string }[]) => (xs || []).map(x => (x.email || '').trim()).filter(Boolean);
  return { to: pick(req.response_recipients?.to), cc: pick(req.response_recipients?.cc) };
}

export function priorSendDisplay(req: { prior_send_badge?: PriorSendBadge | null }): PriorSendDisplay {
  if (!hasPriorSendBadge(req)) return null;
  const text = priorSendBadgeText(req.prior_send_badge);
  if (priorSendBlocksAuto(req)) {
    return { kind: 'block', text, className: 'bg-amber-100 text-amber-900 border-amber-200',
             title: 'Sent before: never sent automatically. Only a per-card Approve sends it.' };
  }
  return { kind: 'info', text, className: 'bg-slate-50 text-slate-600 border-slate-200',
           title: 'For information only: does not hold the card.' };
}

export function isGateEligibleBase(req: GateEligibilityFields): boolean {
  if (req.triage_class !== 'C') return false;
  if (req.auto_verified !== 'zero_claims') return false;
  if (!req.skippy_verified) return false;
  if (req.recipient_verified !== true) return false;
  if (req.archived || req.archived_at) return false;
  if (req.reviewer_notes?.includes('bulk_dedup_sibling')) return false;
  // R2v4 (d)/(ii): a blocking sent-before card never sends automatically (info-only does).
  if (priorSendBlocksAuto(req)) return false;
  // Amos-103 item 2: a client-mismatch (V8) card never sends automatically.
  if (clientMismatchBlocksAuto(req)) return false;
  // Amos-69 (Richard ruling 2026-08-05): duplicate_hold no longer excludes a
  // record from gate eligibility — the dup tag is informational (see
  // RepeatChip in ConvexLossRun). Every request gets a response (standing
  // rule 08-04).
  return true;
}

export function gateHoldReasons(req: GateEligibilityFields): string[] {
  const reasons: string[] = [];
  if (!req.skippy_verified) reasons.push('Unverified');
  if (req.recipient_verified !== true) reasons.push('Recipients unconfirmed');
  if (req.auto_verified !== 'zero_claims') {
    reasons.push('Not verified as zero-claims nil');
  }
  const productCode = req.policy_data?.product_code;
  if (productCode === 'XPT' || productCode === 'CRC') reasons.push(`${productCode} held`);
  if (priorSendBlocksAuto(req)) reasons.push('Sent before — approve individually');
  if (clientMismatchBlocksAuto(req)) reasons.push('Client mismatch — approve on the card only');
  return reasons;
}

// Amos-103 item 5: on the single-card page the amber line explains bulk eligibility only; it is
// not a block on the card (a per-card Approve still works).
export const BULK_ONLY_LABEL = 'Bulk approve only';

export function bulkOnlyHoldLine(req: GateEligibilityFields): string {
  return `${BULK_ONLY_LABEL} — ${gateHoldReasons(req).join(', ') || 'pending checks'}.`;
}

// amos83 Request Portal: portal-created rows carry the deterministic subject "Portal request – <ref>"
// (rule 8). `source` is not in the Lambda list projection yet (rides the next deploy bundle), so the
// B-Review chip keys on the subject prefix — Lambda-free.
export function isPortalRequest(req: { email_subject?: string; source?: string }): boolean {
  return req.source === 'portal' || req.source === 'portal_test' || (req.email_subject || '').startsWith('Portal request');
}

// R2v4 (e): the claim preview reads the stored claim keys. FileHandler claims carry
// claim_status and date_of_notice (the PDF's "Date of Notice" column); the old preview read
// c.status / c.loss_date, which the stored data does not have, so every row showed '--'.
export const CLAIM_PREVIEW_DATE_LABEL = 'Date of Notice';

export function claimPreviewStatus(c: any): string {
  return (c && (c.claim_status || c.status || c.open_close_status)) || '--';
}

export function claimPreviewNoticeDate(c: any): string | undefined {
  return (c && (c.date_of_notice || c.loss_date)) || undefined;
}

// Same output as the PDF cell (handler._fmt_date_mdy): YYYY-MM-DD -> MM/DD/YYYY, read from
// the string (no Date() parse, so a date-only value never shifts a day in US time zones).
export function formatNoticeDate(value?: string | null): string {
  if (!value) return '--';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(value);
}

// Body for POST /convex/requests/{id}/approve. bulk and the reviewed-approve marker are exclusive:
// a bulk call never carries the marker.
export function approveConvexRequestBody(opts?: { overrideDuplicateHold?: boolean; bulk?: boolean; reviewedApprove?: ReviewedApproveMarker | null }): Record<string, unknown> {
  return {
    ...(opts?.overrideDuplicateHold ? { override_duplicate_hold: true } : {}),
    ...(opts?.bulk ? { bulk: true } : {}),
    ...(!opts?.bulk && opts?.reviewedApprove ? { reviewed_approve: opts.reviewedApprove } : {}),
  };
}
